/**
 * @fileoverview "Needs Attention" list on the Dashboard page.
 * @description A clickable row list — a colored tone bar, title/detail, source page, and a status
 * tag — each row navigating to whichever page the item concerns.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useNavigate } from 'react-router'

import Badge from '@/shared/components/atoms/Badge'

import type { AttentionItem } from '@/features/sales/types/sales.types'

interface AttentionListProps {
  items: (AttentionItem & { route: string })[]
}

interface AttentionRowProps {
  item: AttentionItem & { route: string }
  onOpen: (route: string) => void | Promise<void>
}

/**
 * @component
 * @param {AttentionRowProps} props - One attention item and how to open its page.
 * @param {AttentionItem & { route: string }} props.item - The item, already resolved to its route.
 * @param {(route: string) => void | Promise<void>} props.onOpen - Navigates to a route.
 */
function AttentionRow({ item, onOpen }: AttentionRowProps) {
  const rowId = `dashboard-attention-item-${item.id ?? item.title}`

  /**
   * @description Opens the page the item concerns.
   * @returns {void}
   */
  const open = () => void onOpen(item.route)

  return (
    <div data-testid={rowId}>
      <button
        type="button"
        data-testid="dashboard-attention-item-button"
        onClick={open}
        className="grid w-full cursor-pointer grid-cols-[4px_1fr_auto] items-center gap-3.5 px-4 py-3 text-left hover:bg-surface-hover sm:grid-cols-[4px_1.9fr_1.5fr_0.9fr_20px] sm:px-5"
      >
        <span
          className={`h-7.5 w-1 rounded-sm ${item.tone === 'danger' ? 'bg-danger' : 'bg-status-warning'}`}
        />
        <div className="flex min-w-0 flex-col gap-0.5">
          <span
            className="truncate text-[13.5px] font-semibold text-ink"
            data-testid={`${rowId}-title`}
          >
            {item.title}
          </span>
          <span className="truncate text-xs text-footer" data-testid={`${rowId}-detail`}>
            {item.detail}
          </span>
        </div>
        <span
          className="hidden truncate text-[12.5px] text-slate sm:block"
          data-testid={`${rowId}-where`}
        >
          {item.where}
        </span>
        <span className="hidden justify-self-start sm:block" data-testid={`${rowId}-tag`}>
          <Badge variant={item.tone === 'danger' ? 'danger' : 'warning'}>{item.tag}</Badge>
        </span>
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          className="hidden shrink-0 text-input-border sm:block"
          aria-hidden="true"
        >
          <path d="M9 6l6 6-6 6" />
        </svg>
      </button>
    </div>
  )
}

/**
 * @component
 * @param {AttentionListProps} props - The attention items, each already resolved to its
 * destination route.
 * @param {(AttentionItem & { route: string })[]} props.items - The items to render, in order.
 */
function AttentionList({ items }: AttentionListProps) {
  const navigate = useNavigate()

  return (
    <div
      className="divide-y divide-row-border border-t border-divider"
      data-testid="dashboard-attention-list"
    >
      {items.map((item) => (
        <AttentionRow key={item.id ?? item.title} item={item} onOpen={navigate} />
      ))}
    </div>
  )
}

export default AttentionList
